import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Music, Video, Search, Plus, Download, PlayCircle, Trash2, Library, Lock } from "lucide-react";

const TYPE_META = {
  pdf: { icon: FileText, label: "PDF" },
  audio: { icon: Music, label: "Ses" },
  video: { icon: Video, label: "Video" },
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
}

export default function Resources() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/resources", { params: q ? { q } : {} });
      setResources(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const remove = async (id) => {
    if (!window.confirm("Bu kaynağı silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/resources/${id}`);
      toast.success("Kaynak silindi");
      setResources((rs) => rs.filter((r) => r.resource_id !== id));
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div className="flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-earth-400/20 text-earth-500">
            <Library className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-5xl text-ink-900" data-testid="resources-title">
              Kaynak Kütüphanesi
            </h1>
            <p className="font-body text-ink-700">PDF'ler, sesler ve videolar — tam erişim üyelere özel.</p>
          </div>
        </div>
        {user?.role === "admin" && (
          <Link to="/kaynaklar/yeni" className="k-btn-primary" data-testid="new-resource-btn">
            <Plus className="mr-2 h-4 w-4" /> Yeni kaynak
          </Link>
        )}
      </div>

      {!user && (
        <div
          className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-earth-400/30 bg-earth-400/10 p-6 sm:flex-row sm:items-center"
          data-testid="resources-guest-banner"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-earth-400/20 text-earth-500">
              <Lock className="h-5 w-5" />
            </span>
            <p className="font-body text-sm text-ink-700">
              Kaynakları önizleyebilirsin. İndirmek veya izlemek için Kabile'ye katılman gerekiyor.
            </p>
          </div>
          <Link to="/register" className="k-btn-primary shrink-0 text-sm py-2 px-5" data-testid="resources-guest-register-btn">
            Ücretsiz katıl
          </Link>
        </div>
      )}

      <div className="mt-8 relative max-w-md">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Başlık veya açıklamada ara…"
          className="k-input pl-10"
          data-testid="resources-search-input"
        />
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {loading && [1, 2, 3].map((i) => <div key={i} className="k-card h-80 animate-pulse" />)}
        {!loading && resources.length === 0 && (
          <div className="k-card p-10 text-center md:col-span-2 lg:col-span-3" data-testid="resources-empty">
            <p className="font-body text-ink-700">
              {query ? "Eşleşen kaynak bulunamadı." : "Henüz kaynak eklenmedi."}
            </p>
          </div>
        )}
        {!loading &&
          resources.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.pdf;
            const Icon = meta.icon;
            const locked = r.locked;
            return (
              <div
                key={r.resource_id}
                className="k-card group flex flex-col overflow-hidden p-0 transition-all hover:-translate-y-1 hover:shadow-warm"
                data-testid={`resource-card-${r.resource_id}`}
              >
                <div className="relative h-44 w-full shrink-0">
                  {r.cover_image_url ? (
                    <img
                      src={r.cover_image_url}
                      alt={r.title}
                      className="h-full w-full object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center bg-gradient-to-br from-sage-500/25 to-earth-400/25">
                      <Icon className="h-10 w-10 text-sage-700/50" />
                    </div>
                  )}
                  <span className="absolute left-3 top-3 rounded-full bg-cream-50/90 px-2.5 py-1 text-xs font-bold text-ink-700">
                    {meta.label}
                  </span>
                  {locked && (
                    <span
                      className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-cream-50/90 text-ink-700"
                      title="Erişim için üye olman gerekiyor"
                      data-testid={`locked-badge-${r.resource_id}`}
                    >
                      <Lock className="h-4 w-4" />
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-2 text-xs text-ink-500">
                    <span>{formatDate(r.created_at)}</span>
                    {r.file_size && <span>· {formatSize(r.file_size)}</span>}
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-bold text-ink-900">{r.title}</h3>
                  {r.description && (
                    <p className="mt-2 line-clamp-2 flex-1 font-body text-sm text-ink-700">{r.description}</p>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    {locked ? (
                      <Link
                        to="/register"
                        className="k-btn-secondary flex-1 text-center text-sm py-2 px-4"
                        data-testid={`locked-resource-${r.resource_id}`}
                      >
                        <Lock className="mr-2 h-4 w-4" /> Erişmek için katıl
                      </Link>
                    ) : r.type === "video" && r.external_url ? (
                      <a
                        href={r.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="k-btn-secondary flex-1 text-center text-sm py-2 px-4"
                        data-testid={`watch-resource-${r.resource_id}`}
                      >
                        <PlayCircle className="mr-2 h-4 w-4" /> İzle
                      </a>
                    ) : (
                      <a
                        href={`${API}/resources/${r.resource_id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="k-btn-secondary flex-1 text-center text-sm py-2 px-4"
                        data-testid={`download-resource-${r.resource_id}`}
                      >
                        <Download className="mr-2 h-4 w-4" /> İndir
                      </a>
                    )}
                    {user?.role === "admin" && (
                      <button
                        onClick={() => remove(r.resource_id)}
                        className="k-btn-ghost !py-2 !px-2 text-destructive"
                        data-testid={`delete-resource-${r.resource_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
